import React, { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { useGetCompletedMeetingCount, useVerifyCookie } from 'src/hooks/user.hooks';
import { useUserContext } from './UserProvider';
import {
	isUserAccountManager,
	isUserAdmin,
	isUserDriveAdmin,
	isUserJobseeker,
	isUserLeadGen,
	isUserRecruiter,
	isUserUniversityManager,
} from 'shared-utils';
import {
	ADMIN_APP_ENDPOINT,
	RECRUITER_APP_ENDPOINT,
	STUDENT_APP_ENDPOINT,
	UNIVERSITY_APP_ENDPOINT,
	isRecruiterSide,
	isUniversitySide,
} from 'src/config/env.config';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetPositionsForRecruiterQuery, useGetPositionsQuery } from 'src/hooks/position.hooks';
import { usePositionContext } from './PositionProvider';
import { sendTrackingEvent } from 'src/utils/gtm';
import { useMutation } from 'react-query';
import { autoLoginLMSUser, signout } from 'src/apis/auth';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import DeleteUserAccountModal from 'src/components/profile/DeleteUserAccountModal';
import { useRecommendationsContext } from './RecommendationsProvider';
import { useGetMyCompaniesQuery } from 'src/hooks/company.hooks';
import { LMSLinkingStatus, MeetingType, UIPositionStatus, UITemplateConfig, UserRole } from 'types';
import UpdateRoleModal from 'src/components/updateRole/UpdateRoleModal';
import ShowCanLinkLMSProfileModal from 'src/components/profile/ShowCanLinkLMSProfileModal';
import ShowCannotLinkLMSAccountModal from 'src/components/profile/ShowCannotLinkLMSAccountModal';
import ShowLMSConfirmationModal from 'src/components/profile/ShowLMSConfirmationModal';
import { useToast } from './ToastProvider';
import { useGetUserPreferenceMeetingsQuery } from 'src/hooks/meeting.hooks';
import { useStartMeetingMutation } from 'src/hooks/utils/useStartMeetingMutation.hooks';
import BenchMarkingRedirectionModal from 'src/components/scheduleMeetingDrawer/BenchMarkingRedirectionModal';
import BenchMarkingContinueModal from 'src/components/scheduleMeetingDrawer/BenchMarkingContinueModal';
import { useGetHiringAssessment } from 'src/hooks/hiringAssessment.hooks';
import PlacedLoadingAnimation from 'src/components/PlacedLoadingAnimation';
import { useGetAllTemplateConfig } from 'src/hooks/templateConfig.hooks';
type LayoutProps = {
	children: ReactNode;
};

interface LayoutContextOptions {
	isDeleteUserModalOpen: boolean;
	onDeleteUserModalOpen: () => void;
	onDeleteUserModalClose: () => void;
	showLoadingAnimation: boolean;
	setShowLoadingAnimation: Dispatch<SetStateAction<boolean>>;
}
const LayoutContext = createContext<LayoutContextOptions>({
	isDeleteUserModalOpen: false,
	onDeleteUserModalOpen: () => { },
	onDeleteUserModalClose: () => { },
	showLoadingAnimation: false,
	setShowLoadingAnimation: () => { },
});
const LayoutProvider = ({ children }: LayoutProps) => {
	// const { hiringAssessmentId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [showBenchMarkingRedirectionModal, setShowBenchMarkingRedirectionModal] = React.useState(false);
	const [showBenchMarkingContinueModal, setShowBenchMarkingContinueModal] = React.useState(false);
	const { data: templateConfigs } = useGetAllTemplateConfig();
	const [allTemplateConfigs, setAllTemplateConfigs] = React.useState<UITemplateConfig[]>([]);
	useEffect(() => {
		if (templateConfigs) {
			setAllTemplateConfigs(templateConfigs.data);
		}
	}, [templateConfigs, templateConfigs?.data]);

	const {
		isOpen: isDeleteUserModalOpen,
		onOpen: onDeleteUserModalOpen,
		onClose: onDeleteUserModalClose,
	} = useDisclosure();
	const {
		setUser,
		setUserMeetings,
		setUserRounds,
		setTotalCompletedMeetingsCount,
		user,
		setUserLatestScore,
		isJobseekerLoggedIn,
		isRecruiterLoggedIn,
		isUserUniversityManagerLoggedIn,
		isAccountManagerLoggedIn,
		setIsUserDataLoading,
		setShowStartAIInterviewNow,
		setShowCantLinkLMSProfileModal,
		setShowLinkLMSProfileModal,
		setStudentCode,
		setUserPercentileBucket,
		setShowAwaitingResults,
		setIsUserUniversityManagerLoggedIn,
		setTokenLoginErrorMessage,
		setTokenLoginSuccessMessage,
	} = useUserContext();
	const contextUser = user;
	const navigate = useNavigate();
	const count = useGetCompletedMeetingCount(isJobseekerLoggedIn, user?.handler ?? '');
	const { setPositions, setApplications, setIsLoadingPositions } = usePositionContext();
	const {
		companies,
		setCompanies,
		setActiveCompanies,
		setIsLoadingCompanies,
		setTotalCompaniesCount,
		setSelfCompanies,
		setTotalSelfCompaniesCount,
		companyLocationFilterData,
		setTotalActiveCompaniesCount,
		setPositions: setMyPositions,
	} = useRecommendationsContext();
	// Use window.location.search on first paint so token is available before React Router updates; avoids extra get-user-details without Bearer
	const tokenFromUrl =
		typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null;

	const jwtToken = searchParams.get('token') ?? tokenFromUrl ?? null;
	const jwtInternalStudentLinkToken = searchParams.get('lms-token');
	const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);
	const [hiringAssessmentId, setHiringAssessmentId] = useState<string>('');
	const {
		data: preferenceMeetingData,
		isLoading: preferenceMeetingDataLoading,
		refetch: refetchPreferenceMeetingData,
	} = useGetUserPreferenceMeetingsQuery({
		hiringAssessmentId: hiringAssessmentId,
		token: localStorage.getItem('guestUserJwtToken') || '',
	});
	const signoutFn = useMutation(['signout'], signout, {
		onSuccess() {
			window.open(`${STUDENT_APP_ENDPOINT}/auth/signin`, '_self');
		},
	});
	const startMeetingForRoundTemplateMutation = useStartMeetingMutation();
	const toast = useToast();
	const autoLoginLMSUserMutation = useMutation(['autoLoginLmsUser'], autoLoginLMSUser, {
		onSuccess(data) {
			const responseData = data as {
				message: string;
				error: boolean;
				email: string;
				studentCode: string;
				phone: string;
			};
			console.log('Debug: autoLoginLMSUserMutation:', data);
			let message = '';
			if (responseData.message === LMSLinkingStatus.STUDENT_CODE_ALREADY_EXISTS_WITH_ANOTHER_EMAIL) {
				message = 'Student code already exists with another email, please contact support.';
			} else if (
				responseData.message === LMSLinkingStatus.CREATION_FAILED_STUDENT_CODE_ALREADY_EXISTS_WITH_ANOTHER_EMAIL
			) {
				message = 'Student code already exists with another email, please contact support.';
			} else if (responseData.message === LMSLinkingStatus.STUDENT_CODE_SUCCESSFULLY_UPDATED) {
				message = `Student code successfully updated! Please login with email: ${responseData?.email}`;
			} else if (responseData.message === LMSLinkingStatus.PHONE_ALREADY_EXISTS_WITH_ANOTHER_EMAIL) {
				message = 'Phone number already exists with another email, please contact support.';
			} else if (responseData.message === LMSLinkingStatus.ACCOUNT_SUCCESSFULLY_CREATED) {
				message = `Account successfully created! Please login with email: ${responseData?.email}`;
			}
			console.log('Debug: autoLoginLMSUserMutation message:', message);
			localStorage.setItem('lmsLevelupIntegrationMessage', message);
			signoutFn.mutate();
		},
		onError(err) {
			toast({
				title: `Error while linking LMS account with Levelup`,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		// get the path param from url
		const pathParams = window.location.pathname.split('/');
		if (!pathParams?.includes('placed-hiring-assessment')) {
			return;
		}
		const hiringAssessmentId = pathParams[pathParams.length - 1];
		if (hiringAssessmentId.includes('-')) {
			return;
		}
		setHiringAssessmentId(hiringAssessmentId);
		console.log('hiringAssessmentId:', hiringAssessmentId);
		refetchPreferenceMeetingData();
	}, [window.location.pathname]);

	const lmsAccountToken = searchParams.get('lms-account-token');
	useEffect(() => {
		if (lmsAccountToken) {
			autoLoginLMSUserMutation.mutate({ token: lmsAccountToken });
		}
	}, [lmsAccountToken]);

	const { isLoading: isVerifyCookieLoading, isError: isVerifyCookieError, error: verifyCookieError } = useVerifyCookie(
		{
			jwtToken: jwtToken || undefined,
			jwtInternalStudentLinkToken: jwtInternalStudentLinkToken || undefined,
		},
		data => {
			const user = data.user;

			// LMS token login failed (invalid token or cookie invalid) — show error and clear token
			if (jwtToken && (data as { error?: boolean; message?: string })?.error && (data as { message?: string })?.message) {
				setTokenLoginErrorMessage((data as { message: string }).message);
				searchParams.delete('token');
				setSearchParams(searchParams);
				return;
			}

			if (user && user.subRole === 'Onwards' && isUserJobseeker(user.role)) {
				setSearchParams({});
			} else if (user && !jwtToken) {
				// Only clear token from URL when we had cookie auth (not token auth), so token login keeps working on localhost/cross-origin
				searchParams.delete('token');
				setSearchParams(searchParams);
			}
			// if user account deactivated
			if (user?.flags?.isDeletedAccount) {
				signoutFn.mutate();
				return;
			}

			if (user && !window.location.pathname.startsWith('/linkedin')) {
				// When user just logged in via URL token, stay on current page (same origin) so session/token keeps working. Do not redirect to another port.
				if (jwtToken) {
					if (count.data?.meetingCount) setTotalCompletedMeetingsCount(count.data?.meetingCount);
					setUser(user);
					const signedInEmail = user?.email ?? '';
					const switchedAccount = contextUser && contextUser._id !== user._id;
					setTokenLoginSuccessMessage(
						switchedAccount
							? `Logged out previous account. Signed in as ${signedInEmail}.`
							: `You have been signed in as ${signedInEmail}.`,
					);
				} else if (process.env.REACT_APP_NODE_ENV !== 'development') {
					if (isUserAdmin(user.role)) {
						if (
							!window.location.pathname.includes('/jobseeker/placed-hiring-assessment') &&
							!window.location.pathname.startsWith('/jobseeker/profile') &&
							!window.location.pathname.startsWith('/jobseeker/job') &&
							!window.location.pathname.startsWith('/jobseeker/hosts/sessions/live')
						) {
							window.open(`${ADMIN_APP_ENDPOINT}`, '_self');
						} else {
							setUser(user);
						}
					} else if (
						(isUserRecruiter(user.role) || isUserAccountManager(user.role) || isUserDriveAdmin(user.role) || isUserLeadGen(user.role)) &&
						!isRecruiterSide
					) {
						if (!window.location.pathname.startsWith('/jobseeker/profile')) {
							window.open(`${RECRUITER_APP_ENDPOINT}`, '_self');
						}
					} else if (isUserUniversityManager(user.role) && !isUniversitySide) {
						if (!window.location.pathname.startsWith('/jobseeker/profile')) {
							window.open(`${UNIVERSITY_APP_ENDPOINT}`, '_self');
						}
					} else if (
						(!isUserRecruiter(user.role) &&
							!isUserAccountManager(user.role) &&
							!isUserDriveAdmin(user.role) &&
							!isUserLeadGen(user.role) &&
							isRecruiterSide) ||
						(!isUserUniversityManager(user.role) && isUniversitySide)
					) {
						window.open(`${STUDENT_APP_ENDPOINT}`, '_self');
					} else {
						if (count.data?.meetingCount) setTotalCompletedMeetingsCount(count.data?.meetingCount);
						setUser(user);
					}
				} else {
					if (isUserAdmin(user.role)) {
						if (
							!window.location.pathname.startsWith('/jobseeker/placed-hiring-assessment') &&
							!window.location.pathname.startsWith('/profile') &&
							!window.location.pathname.startsWith('/jobseeker/job') &&
							!window.location.pathname.startsWith('/hosts/sessions/live')
						) {
							console.log('Debug: user:', user);
							console.log('Debug: ADMIN_APP_ENDPOINT:', ADMIN_APP_ENDPOINT);
							window.open(`${ADMIN_APP_ENDPOINT}`, '_self');
						} else {
							setUser(user);
						}
					} else {
						if (count.data?.meetingCount) setTotalCompletedMeetingsCount(count.data?.meetingCount);
						setUser(user);
					}
				}

				sendTrackingEvent({
					event: 'ga_login',
					user_id: user._id,
				});
			} else if (
				!window.location.pathname.includes('/profile') &&
				!window.location.pathname.includes('/job') &&
				!window.location.pathname.includes('/auth') &&
				!window.location.pathname.includes('/linkedin') &&
				!window.location.pathname.includes('/hosts') &&
				!window.location.pathname.includes('/placed-hiring-assessment') &&
				!window.location.pathname.includes('/offline-drive-attendance') &&
				!window.location.pathname.includes('/auth') &&
				!window.location.pathname.includes('/offline-drive-attendance') &&
				!window.location.pathname.includes('/linkedin-auth') &&
				!window.location.pathname.includes('/hosts/sessions/live') &&
				!window.location.pathname.includes('/scorecard/') &&
				!window.location.pathname.includes('/offline-drive-day-opportunities')
			) {
				console.log('Redirecting to signin');
				// window.open(`/auth/signin?redirect_url=${encodeURIComponent(window.location.href)}`, '_self');
			}
			setUserMeetings(data.meetings ?? []);
			setUserRounds(data.rounds ?? []);
			setUserLatestScore(data.latestMeetingScore);
			setShowAwaitingResults(data.awaitingMeetingScores || false);
			setShowStartAIInterviewNow(data.showStartAIInterviewNow || false);
			setShowCantLinkLMSProfileModal(data.showCantLinkLMSProfileModal || false);
			setShowLinkLMSProfileModal(data.showLinkLMSProfileModal || false);
			setStudentCode(data.studentCode || '');
			setUserPercentileBucket(data.userPercentileBucket || null);
			if (!data.showCantLinkLMSProfileModal && !data.showLinkLMSProfileModal) {
				searchParams.delete('lms-token');
				setSearchParams(searchParams);
			}
		},
		// Show toast when token login fails (401/403: no Levelup account, invalid/expired link, not jobseeker)
		error => {
			if (!jwtToken) return;
			const err = error as { response?: { data?: { message?: string } } };
			const message = err?.response?.data?.message || 'Login with link failed. Please sign in manually.';
			setTokenLoginErrorMessage(message);
			searchParams.delete('token');
			setSearchParams(searchParams);
		},
	);

	// Show API error when LMS token login fails (user not found or not jobseeker)
	useEffect(() => {
		if (jwtToken && isVerifyCookieError && verifyCookieError) {
			const err = verifyCookieError as { response?: { data?: { message?: string } } };
			const message = err?.response?.data?.message || 'Login with link failed. Please sign in manually.';
			setTokenLoginErrorMessage(message);
			searchParams.delete('token');
			setSearchParams(searchParams);
		}
	}, [jwtToken, isVerifyCookieError, verifyCookieError, setTokenLoginErrorMessage]);

	useEffect(() => {
		setIsUserDataLoading(isVerifyCookieLoading);
	}, [isVerifyCookieLoading]);

	const { isLoading: isCompaniesLoading, isFetching: isCompaniesFetching } = useGetMyCompaniesQuery(
		isAccountManagerLoggedIn,
		{
			limit: 12,
			isDisqualified: searchParams.get('isDisqualified') === 'true' || false,
			isQualified: searchParams.get('isQualified') === 'true' || false,
			isPendingAction: searchParams.get('isPendingAction') === 'true' || false,
			selfCreatedPosition: searchParams.get('selfCreatedPosition') === 'true' || false,
			page: +(searchParams.get('page') || 1),
			tab: window.location.pathname.includes('companies')
				? (searchParams.get('tab') as UIPositionStatus) || UIPositionStatus.ACTIVE
				: UIPositionStatus.ACTIVE,
			showNewlyCreated: searchParams.get('showNewlyCreated') === 'true',
			name: searchParams.get('name') || '',
			locations: String(companyLocationFilterData),
		},
		data => {
			setCompanies(data?.companies ?? []);
			setActiveCompanies(data?.activeCompanies ?? []);
			setTotalCompaniesCount(data?.total || 0);
			setSelfCompanies(data?.selfCompanies ?? []);
			setTotalSelfCompaniesCount(data?.self || 0);
			setTotalActiveCompaniesCount(data?.active || 0);
		},
	);

	const { refetch: refetchPositions } = useGetPositionsForRecruiterQuery(
		isAccountManagerLoggedIn,
		companies.length ? companies[0].company._id : '',
		data => setMyPositions(data.positions ?? []),
	);

	const { isLoading } = useGetPositionsQuery(isJobseekerLoggedIn, data => {
		setPositions({
			eligiblePositions: data.eligiblePositions || [],
			inEligiblePositions: data.inEligiblePositions ?? [],
			closedPositions: data.closedPositions ?? [],
			activeApplicantCount: data.activeApplicantCount ?? 0,
		});
		setApplications(data?.applications ?? []);
	});

	useEffect(() => {
		setIsLoadingPositions(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	useEffect(() => {
		setIsLoadingCompanies(isCompaniesLoading || isCompaniesFetching);
	}, [isCompaniesLoading, isCompaniesFetching]);

	useEffect(() => {
		if (!user?.role) {
			return;
		}
		refetchPositions();
	}, [isRecruiterLoggedIn, user?.role]);

	useEffect(() => {
		console.log('Debug: hiringAssessmentId', hiringAssessmentId);
	}, [hiringAssessmentId]);

	const {
		data: hiringAssessmentData,
		isLoading: hiringAssessmentLoading,
		refetch: refetchHiringAssessmentData,
	} = useGetHiringAssessment({
		hiringAssessmentId: hiringAssessmentId || '',
		token: localStorage.getItem('guestUserJwtToken') || '',
	});

	const handleSecondTestRedirect = () => {
		try {
			const hiringAssessmentId = hiringAssessmentData?.hiringAssessment?._id;
			let templates = [];
			console.log(
				'handleSecondTestRedirect Debug: hiringAssessmentData',
				hiringAssessmentId,
				hiringAssessmentData,
				hiringAssessmentData?.hiringAssessment?.templates,
			);
			if (hiringAssessmentId) {
				if (process.env.REACT_APP_NODE_ENV !== 'production') {
					templates = ['66c61c4f9d4dc23410241fb3', '66c61da59d4dc2341024c14f'];
				} else {
					templates = hiringAssessmentData?.hiringAssessment?.templates || [];
				}
			} else {
				const userPreference = user?.userPreference;
				templates =
					allTemplateConfigs.find(
						config =>
							config.experienceLevel === userPreference?.experienceLevel &&
							config.careerPath === userPreference?.careerPath &&
							config.developmentRole === userPreference?.developmentRole &&
							config.techStack === userPreference?.techStack,
					)?.templates || [];
			}

			// Validate templates array exists and has required templates
			if (!templates || templates.length === 0) {
				console.error('handleSecondTestRedirect: No templates found', {
					hiringAssessmentId,
					userPreference: user?.userPreference,
					allTemplateConfigs,
				});
				toast({
					title: 'Unable to start assessment. Please contact support.',
					type: 'error',
				});
				setShowBenchMarkingRedirectionModal(false);
				return;
			}

			const completedMeetingType = preferenceMeetingData?.completedMeetings?.[0]?.meetingType;
			const templateIndex = completedMeetingType === MeetingType.Interview ? 1 : 0;

			// Validate template exists at required index
			if (!templates[templateIndex]) {
				console.error(`handleSecondTestRedirect: Template at index ${templateIndex} not found`, {
					templates,
					completedMeetingType,
					templateIndex,
				});
				toast({
					title: 'Unable to start assessment. Please contact support.',
					type: 'error',
				});
				setShowBenchMarkingRedirectionModal(false);
				return;
			}

			console.log('handleSecondTestRedirect: Starting meeting with template', {
				templateId: templates[templateIndex],
				hiringAssessmentId,
				uniqueTestId: preferenceMeetingData?.completedMeetings?.[0]?.uniqueTestId,
			});

			startMeetingForRoundTemplateMutation.mutate(
				{
					roundTemplateId: templates[templateIndex],
					isMock: false,
					token: localStorage.getItem('guestUserJwtToken') || '',
					hiringAssessmentId: hiringAssessmentId ? hiringAssessmentId : undefined,
					uniqueTestId: preferenceMeetingData?.completedMeetings?.[0]?.uniqueTestId || undefined,
				},
				{
					onError: (error: any) => {
						console.error('handleSecondTestRedirect mutation failed:', error);
						toast({
							title: error?.response?.data?.message || 'Failed to start assessment. Please try again.',
							type: 'error',
						});
						setShowBenchMarkingRedirectionModal(false);
					},
				},
			);
		} catch (error) {
			console.error('handleSecondTestRedirect error:', error);
			toast({
				title: 'Error starting meeting in layout provider',
				type: 'error',
			});
			setShowBenchMarkingRedirectionModal(false);
		}
	};

	useEffect(() => {
		if (preferenceMeetingDataLoading) return;
		console.log('Debug: preferenceMeetingData', preferenceMeetingData);
		// User has completed both meeting and now prompt user to fill optional form data
		console.log('Debug: preferenceMeetingData?.completedMeetings', hiringAssessmentId);
		if (preferenceMeetingData?.completedMeetings?.length === 2 && !hiringAssessmentId) {
			// redirect to optional form data page and save in localstorage
			// check if localstorage has already been redirected
			const optionalFormDataRedirectDone = localStorage.getItem(
				`optionalFormDataRedirectDone-${user?._id}-${preferenceMeetingData?.completedMeetings[0]._id}-${preferenceMeetingData?.completedMeetings[1]._id}`,
			);
			if (optionalFormDataRedirectDone !== 'true') {
				localStorage.setItem(
					`optionalFormDataRedirectDone-${user?._id}-${preferenceMeetingData?.completedMeetings[0]._id}-${preferenceMeetingData?.completedMeetings[1]._id}`,
					'true',
				);
				navigate('/benchmarking/optional-details');
			}
		} else if (preferenceMeetingData?.ongoingAssessment?.interviewUrl) {
			setShowBenchMarkingContinueModal(true);
		} else if (
			preferenceMeetingData?.completedMeetings?.length === 1 &&
			preferenceMeetingData?.activeMeetings?.length === 0
		) {
			setShowBenchMarkingRedirectionModal(true);
		}
	}, [
		preferenceMeetingData?.activeMeetings,
		preferenceMeetingData?.completedMeetings,
		preferenceMeetingData?.completedMeetings?.length,
		preferenceMeetingData?.activeMeetings?.length,
		preferenceMeetingData?.ongoingAssessment?.interviewUrl,
		preferenceMeetingDataLoading,
		user?._id,
		user?.userPreference,
		hiringAssessmentId,
	]);

	return (
		<LayoutContext.Provider
			value={{
				isDeleteUserModalOpen,
				onDeleteUserModalOpen,
				onDeleteUserModalClose,
				showLoadingAnimation,
				setShowLoadingAnimation,
			}}
		>
			{children}
			<DeleteUserAccountModal />
			<ShowCanLinkLMSProfileModal />
			<ShowCannotLinkLMSAccountModal />
			<ShowLMSConfirmationModal />
			<UpdateRoleModal />
			{showBenchMarkingRedirectionModal && (
				<BenchMarkingRedirectionModal
					showBenchMarkingRedirectionModal={showBenchMarkingRedirectionModal}
					setShowBenchMarkingRedirectionModal={setShowBenchMarkingRedirectionModal}
					handleSecondTestRedirect={handleSecondTestRedirect}
					isLoading={startMeetingForRoundTemplateMutation.isLoading}
				/>
			)}
			{/* {showBenchMarkingContinueModal && (
				<BenchMarkingContinueModal
					showBenchMarkingContinueModal={showBenchMarkingContinueModal}
					setShowBenchMarkingContinueModal={setShowBenchMarkingContinueModal}
					pendingTestUrl={preferenceMeetingData?.ongoingAssessment?.interviewUrl || ''}
				/>
			)} */}
			{showLoadingAnimation ? <PlacedLoadingAnimation showModal={true} /> : null}
		</LayoutContext.Provider>
	);
};

export default LayoutProvider;

export const useLayoutContext = () => {
	const context = useContext(LayoutContext);
	return context;
};
